import React, { useMemo, useState } from 'react';
import { downloadInvoicePdf } from '../../utils/downloadInvoicePdf';
import { useDirectInvoices } from '../../hooks/useDirectInvoices';
import { useOrgDirectInvoices } from '../../hooks/useOrgDirectInvoices';
import { useOrgMembership } from '../../hooks/useOrgMembership';
import { useInvoiceFeature } from '../../hooks/useInvoiceFeature';
import InvoiceStatusChip from '../common/InvoiceStatusChip';
import { formatKr } from '../../utils/formatKr';
import { COLORS, FONTS } from '../../styles/tokens';

// Fakturaer: utestående beløp. Kreditnotaer: negativt beløp.
function amountOf(inv) {
  return inv.kind === 'credit-note' ? inv.grossAmount : inv.grossAmount - (inv.creditedGross ?? 0);
}

const millis = (inv) => inv.createdAt?.toDate?.()?.getTime?.() ?? 0;

// Bestillerens fakturaer fra sjåfører (egne + org-betalte fra kollegaer). Ikke del av «Forbruk i måneden».
export default function DirectInvoicesSection() {
  const { enabled } = useInvoiceFeature();
  const { org, membership } = useOrgMembership();
  const orgId = org?.id && membership?.status === 'active' ? org.id : null;
  const own = useDirectInvoices('orderer');
  const orgInv = useOrgDirectInvoices(orgId);
  const [busy, setBusy]   = useState(false); // én handling om gangen
  const [error, setError]   = useState('');

  const invoices = useMemo(() => {
    const byId = new Map();
    [...own.invoices, ...orgInv.invoices].forEach(i => byId.set(i.id, i));
    return [...byId.values()].sort((a, b) => millis(b) - millis(a));
  }, [own.invoices, orgInv.invoices]);

  const loading = own.loading || (orgId ? orgInv.loading : false);

  if (!enabled && invoices.length === 0) return null;

  async function download(inv) {
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      await downloadInvoicePdf(inv.id);
    } catch (e) {
      setError(e?.message || 'Noe gikk galt. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={styles.card} aria-label="Fakturaer fra sjåfører">
      <h2 style={styles.title}>Fakturaer fra sjåfører</h2>

      {error && <p role="alert" style={styles.error}>{error}</p>}
      {loading && invoices.length === 0 && <p style={styles.empty}>Laster fakturaer…</p>}
      {!loading && invoices.length === 0 && <p style={styles.empty}>Ingen fakturaer ennå.</p>}

      {invoices.map(inv => {
        const isNote = inv.kind === 'credit-note';
        const due = inv.dueDate?.toDate?.();
        return (
          <div key={inv.id} style={styles.row} data-testid={`inv-${inv.id}`}>
            <div style={styles.top}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.number}>{isNote ? 'Kreditnota' : 'Faktura'} {inv.invoiceNumber}</div>
                <div style={styles.meta}>{inv.issuer?.name}</div>
                {!isNote && due && (
                  <div style={styles.meta}>
                    Forfall {due.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={styles.amount} data-testid={`amount-${inv.id}`}>{formatKr(amountOf(inv))}</div>
                <InvoiceStatusChip invoice={inv} />
              </div>
            </div>
            <div style={styles.actions}>
              <button type="button" style={styles.btn} disabled={busy}
                      onClick={() => download(inv)}>Last ned PDF</button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

const styles = {
  card:    { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: 18, marginTop: 16 },
  title:   { fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, margin: '0 0 4px', color: COLORS.textDark },
  error:   { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 10, fontSize: 14, margin: '8px 0' },
  empty:   { fontSize: 14, color: COLORS.textLight, margin: '8px 0 0' },
  row:     { padding: '14px 0', borderBottom: `1px solid ${COLORS.border}` },
  top:     { display: 'flex', alignItems: 'flex-start', gap: 12 },
  number:  { fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  meta:    { fontSize: 12.5, color: COLORS.textMid, marginTop: 2 },
  amount:  { fontFamily: FONTS.display, fontWeight: 700, fontSize: 22, lineHeight: 1, marginBottom: 5, color: COLORS.textDark },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  btn:     { borderRadius: 20, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', background: COLORS.white, border: `1px solid ${COLORS.border}`, color: COLORS.textDark },
};
